/**
 * CampaignsPage.tsx
 *
 * Full email campaign management page.
 * Lists campaigns with search, status filter, and per-row actions.
 * Supports create/edit via modal and delete via confirm dialog.
 * Includes: email body editor, recipient management, preview, schedule, and stats.
 */
import type React from 'react';
import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Eye } from 'lucide-react';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn, DataTableAction } from '@/components/ui/DataTable';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { FormField } from '@/components/ui/FormField';
import { Textarea } from '@/components/ui/Textarea';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { StatCard } from '@/components/ui/StatCard';
import { Tabs } from '@/components/ui/Tabs';
import type { BadgeVariant } from '@/types/common.types';
import type { FilterConfig, FilterOption, FilterValue } from '@/types/filters.types';
import { usePageContext } from '@/hooks/usePageContext';
import * as campaignsSvc from '@/services/campaigns.service';
import type { Campaign, CreateCampaignData, UpdateCampaignData } from '@/services/campaigns.service';
import { DateTimePicker, nowAsDateTimeValue } from '@/components/ui/DateTimePicker';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '\u2014';
  return new Date(dateStr).toLocaleDateString();
}

function formatDateTimeLocal(dateStr: string | null): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const STATUS_BADGE_MAP: Record<string, BadgeVariant> = {
  draft:     'neutral',
  scheduled: 'info',
  sending:   'warning',
  sent:      'success',
  cancelled: 'error',
};

const FILTER_CONFIGS: FilterConfig[] = [
  { key: 'search', label: 'Search', type: 'search', allLabel: 'Search campaigns...' },
  { key: 'status', label: 'Status', type: 'select', allLabel: 'All statuses' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'sending', label: 'Sending' },
  { value: 'sent', label: 'Sent' },
  { value: 'cancelled', label: 'Cancelled' },
];

const EDITABLE_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'cancelled', label: 'Cancelled' },
];

const RECIPIENT_TYPE_OPTIONS = [
  { value: 'all_patients', label: 'All Patients' },
  { value: 'active_patients', label: 'Active Patients' },
  { value: 'by_tag', label: 'By Tag' },
  { value: 'manual', label: 'Manual Selection' },
];

// ─── Preview Modal ────────────────────────────────────────────────────────────

interface PreviewModalProps {
  subject: string;
  body: string;
  fromName: string;
  onClose: () => void;
}

function PreviewModal({ subject, body, fromName, onClose }: PreviewModalProps) {
  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Campaign Preview"
      size="lg"
      footer={
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        {/* Email header */}
        <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 space-y-1">
          <div className="text-xs text-slate-500">
            <span className="font-medium text-slate-700">From:</span> {fromName || 'Your Practice'}
          </div>
          <div className="text-xs text-slate-500">
            <span className="font-medium text-slate-700">Subject:</span> {subject || '(No subject)'}
          </div>
        </div>
        {/* Email body */}
        <div className="px-4 py-6 min-h-[200px]">
          {body ? (
            <div
              className="prose prose-sm max-w-none text-slate-700"
              dangerouslySetInnerHTML={{ __html: body }}
            />
          ) : (
            <p className="text-sm text-slate-400 italic">No email body content yet.</p>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ─── Campaign Modal ──────────────────────────────────────────────────────────

interface CampaignModalProps {
  campaign?: Campaign | null;
  onClose: () => void;
  onSaved: () => void;
}

function CampaignModal({ campaign, onClose, onSaved }: CampaignModalProps) {
  const isEdit = campaign !== null && campaign !== undefined;

  const [name, setName]           = useState(isEdit ? campaign.name : '');
  const [subject, setSubject]     = useState(isEdit ? campaign.subject : '');
  const [description, setDescription] = useState(isEdit ? (campaign.description ?? '') : '');
  const [emailBody, setEmailBody] = useState(isEdit ? (campaign.emailBody ?? '') : '');
  const [fromName, setFromName]   = useState(isEdit ? (campaign.fromName ?? '') : '');
  const [fromEmail, setFromEmail] = useState(isEdit ? (campaign.fromEmail ?? '') : '');
  const [replyTo, setReplyTo]     = useState(isEdit ? (campaign.replyTo ?? '') : '');
  const [status, setStatus]       = useState(isEdit ? campaign.status : 'draft');
  const [scheduledAt, setScheduledAt] = useState(isEdit ? formatDateTimeLocal(campaign.scheduledAt) : '');
  const [recipientType, setRecipientType] = useState(isEdit ? (campaign.recipientType ?? 'all_patients') : 'all_patients');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !subject.trim()) return;
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        const data: UpdateCampaignData = {
          name,
          subject,
          description: description || null,
          emailBody: emailBody || null,
          fromName: fromName || null,
          fromEmail: fromEmail || null,
          replyTo: replyTo || null,
          status,
          scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : null,
          recipientType: recipientType || null,
        };
        await campaignsSvc.updateCampaign(campaign.campaignId, data);
      } else {
        const data: CreateCampaignData = {
          name,
          subject,
          description: description || null,
          emailBody: emailBody || null,
          fromName: fromName || null,
          fromEmail: fromEmail || null,
          replyTo: replyTo || null,
          status,
          scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : null,
          recipientType: recipientType || null,
        };
        await campaignsSvc.createCampaign(data);
      }
      onSaved();
      onClose();
    } catch {
      setError('Failed to save campaign. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  const detailsTab = (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Campaign Name" required>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Spring Promotion"
            required
            autoFocus
          />
        </FormField>
        <FormField label="Status">
          <Select
            value={status}
            onChange={(v) => setStatus(v ?? 'draft')}
            options={EDITABLE_STATUS_OPTIONS}
          />
        </FormField>
      </div>
      <FormField label="Subject" required>
        <Input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Email subject line"
          required
        />
      </FormField>
      <FormField label="Description">
        <Textarea autoResize
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Internal description of this campaign"
          rows={3}
        />
      </FormField>
      <FormField label="Scheduled Date / Time" htmlFor="campaign-scheduled-at">
        <DateTimePicker
          id="campaign-scheduled-at"
          value={scheduledAt}
          min={nowAsDateTimeValue()}
          onChange={setScheduledAt}
        />
        {scheduledAt && (
          <p className="text-xs text-slate-500 mt-1">
            Scheduled for: {new Date(scheduledAt).toLocaleString()}
          </p>
        )}
      </FormField>
      <div className="grid grid-cols-3 gap-4">
        <FormField label="From Name">
          <Input
            value={fromName}
            onChange={(e) => setFromName(e.target.value)}
            placeholder="Sender name"
          />
        </FormField>
        <FormField label="From Email">
          <Input
            type="email"
            value={fromEmail}
            onChange={(e) => setFromEmail(e.target.value)}
            placeholder="sender@example.com"
          />
        </FormField>
        <FormField label="Reply To">
          <Input
            type="email"
            value={replyTo}
            onChange={(e) => setReplyTo(e.target.value)}
            placeholder="reply@example.com"
          />
        </FormField>
      </div>
    </div>
  );

  const emailBodyTab = (
    <div className="space-y-2">
      <FormField label="Email Body">
        <Textarea
          value={emailBody}
          onChange={(e) => setEmailBody(e.target.value)}
          placeholder="Write the email content here. You can use HTML for formatting."
          rows={12}
        />
      </FormField>
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span>{emailBody.length.toLocaleString()} characters</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowPreview(true)}
          disabled={!subject.trim() && !emailBody.trim()}
        >
          <Eye className="h-3.5 w-3.5 mr-1" />
          Preview
        </Button>
      </div>
    </div>
  );

  const recipientsTab = (
    <div className="space-y-4">
      <FormField label="Recipient Selection">
        <Select
          value={recipientType}
          onChange={(v) => setRecipientType(v ?? 'all_patients')}
          options={RECIPIENT_TYPE_OPTIONS}
        />
      </FormField>
      {recipientType === 'all_patients' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
          <p className="text-sm text-blue-800 font-medium">All Patients</p>
          <p className="text-xs text-blue-600 mt-1">
            This campaign will be sent to all patients in the system with a valid email address.
          </p>
        </div>
      )}
      {recipientType === 'active_patients' && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3">
          <p className="text-sm text-green-800 font-medium">Active Patients</p>
          <p className="text-xs text-green-600 mt-1">
            This campaign will be sent to patients with an active status and a valid email address.
          </p>
        </div>
      )}
      {recipientType === 'by_tag' && (
        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            <p className="text-sm text-amber-800 font-medium">By Tag</p>
            <p className="text-xs text-amber-600 mt-1">
              Select patient tags to target specific groups. Patients matching any selected tag will receive this campaign.
            </p>
          </div>
          <Input
            placeholder="Search tags to add..."
          />
        </div>
      )}
      {recipientType === 'manual' && (
        <div className="space-y-3">
          <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-3">
            <p className="text-sm text-purple-800 font-medium">Manual Selection</p>
            <p className="text-xs text-purple-600 mt-1">
              Search and add individual patients to this campaign's recipient list.
            </p>
          </div>
          <Input
            placeholder="Search patients by name or email..."
          />
        </div>
      )}
      {isEdit && (
        <div className="mt-4 pt-4 border-t border-slate-200">
          <p className="text-sm text-slate-600">
            <span className="font-medium text-slate-800">Total Recipients:</span>{' '}
            {campaign.totalRecipients.toLocaleString()}
          </p>
        </div>
      )}
    </div>
  );

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        title={isEdit ? 'Edit Campaign' : 'Create Campaign'}
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="ghost"
              onClick={() => setShowPreview(true)}
              disabled={!subject.trim() && !emailBody.trim()}
            >
              <Eye className="h-4 w-4 mr-1" />
              Preview
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={saving || !name.trim() || !subject.trim()}
              loading={saving}
            >
              {isEdit ? 'Save Changes' : 'Create Campaign'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2 mb-4">
              {error}
            </p>
          )}
          <Tabs
            tabs={[
              { id: 'details', label: 'Details', content: detailsTab },
              { id: 'email-body', label: 'Email Body', content: emailBodyTab },
              { id: 'recipients', label: 'Recipients', content: recipientsTab },
            ]}
            defaultTab="details"
          />
        </form>
      </Modal>

      {showPreview && (
        <PreviewModal
          subject={subject}
          body={emailBody}
          fromName={fromName}
          onClose={() => setShowPreview(false)}
        />
      )}
    </>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function CampaignsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const updateAIContext = usePageContext('Campaigns');

  // ── Filter / pagination state ──────────────────────────────────────────
  const [search, setSearch]           = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage]               = useState(1);
  const pageSize                      = 25;

  // ── Modal state ────────────────────────────────────────────────────────
  const [showCreate, setShowCreate]         = useState(false);
  const [editCampaign, setEditCampaign]     = useState<Campaign | null>(null);
  const [deleteCampaign, setDeleteCampaign] = useState<Campaign | null>(null);
  const [previewCampaign, setPreviewCampaign] = useState<Campaign | null>(null);

  // ── Query ──────────────────────────────────────────────────────────────
  const params = {
    search: search.trim() || undefined,
    status: statusFilter || undefined,
    page,
    pageSize,
  };

  const { data, isLoading } = useQuery({
    queryKey: ['campaigns', params],
    queryFn: () => campaignsSvc.listCampaigns(params),
    placeholderData: (prev) => prev,
  });

  const { data: campaignStats } = useQuery({
    queryKey: ['campaigns-stats'],
    queryFn: () => campaignsSvc.getCampaignStats(),
    staleTime: 60_000,
  });

  // ── Stats ──────────────────────────────────────────────────────────────
  const stats = {
    total:          campaignStats?.total          ?? data?.meta?.total ?? 0,
    drafts:         campaignStats?.drafts         ?? 0,
    sent:           campaignStats?.sent           ?? 0,
    totalRecipients: campaignStats?.totalRecipients ?? 0,
  };

  // ── AI page context ────────────────────────────────────────────────────
  useEffect(() => {
    updateAIContext({
      filters: `Search: ${search || 'none'}, Status: ${statusFilter || 'all'}`,
      dataSummary: `${stats.total} campaigns, ${stats.drafts} drafts, ${stats.sent} sent`,
    });
  }, [search, statusFilter, stats.total, stats.drafts, stats.sent, updateAIContext]);

  // ── Delete mutation ────────────────────────────────────────────────────
  const deleteMutation = useMutation({
    mutationFn: (id: number) => campaignsSvc.deleteCampaign(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      setDeleteCampaign(null);
    },
  });

  const handleSaved = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
  }, [queryClient]);

  // ── Sidebar filter plumbing ─────────────────────────────────────────────
  const sidebarOptions = useMemo<Record<string, FilterOption[]>>(
    () => ({
      status: STATUS_OPTIONS.filter((o) => o.value !== '').map((o) => ({ value: o.value, label: o.label })),
    }),
    [],
  );

  const sidebarValues = useMemo<Record<string, FilterValue>>(
    () => ({
      search: search ? [search] : [],
      status: statusFilter ? [statusFilter] : [],
    }),
    [search, statusFilter],
  );

  const handleSidebarChange = useCallback((filterKey: string, value: FilterValue) => {
    if (filterKey === 'search' && Array.isArray(value)) {
      setSearch(value[0] ?? '');
      setPage(1);
    }
    if (filterKey === 'status' && Array.isArray(value)) {
      setStatusFilter(value[0] ?? '');
      setPage(1);
    }
  }, []);

  const handleSidebarClear = useCallback((filterKey: string) => {
    if (filterKey === 'search') {
      setSearch('');
      setPage(1);
    }
    if (filterKey === 'status') {
      setStatusFilter('');
      setPage(1);
    }
  }, []);

  // ── Table columns ──────────────────────────────────────────────────────
  const columns: DataTableColumn<Campaign>[] = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (row) => (
        <span className="font-medium text-slate-900">{row.name}</span>
      ),
    },
    {
      key: 'subject',
      header: 'Subject',
      render: (row) => (
        <span className="text-sm text-slate-700 line-clamp-1">{row.subject}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <Badge variant={STATUS_BADGE_MAP[row.status] ?? 'neutral'}>
          {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
        </Badge>
      ),
    },
    {
      key: 'totalRecipients',
      header: 'Recipients',
      align: 'right',
      render: (row) => (
        <span className="text-sm text-slate-700">{(row.totalRecipients ?? 0).toLocaleString()}</span>
      ),
    },
    {
      key: 'scheduledAt',
      header: 'Scheduled',
      render: (row) => (
        <span className="text-sm text-slate-600">{formatDate(row.scheduledAt)}</span>
      ),
    },
    {
      key: 'sentAt',
      header: 'Sent',
      render: (row) => (
        <span className="text-sm text-slate-600">{formatDate(row.sentAt)}</span>
      ),
    },
    {
      key: 'createdByName',
      header: 'Created By',
      render: (row) => (
        <span className="text-sm text-slate-600">{row.createdByName ?? '\u2014'}</span>
      ),
    },
  ];

  function handleRowClick(row: Campaign) {
    if (row.status === 'draft' || row.status === 'scheduled') {
      navigate(`/marketing/campaigns/${row.campaignId}`);
    }
  }

  const rowActions: DataTableAction<Campaign>[] = [
    {
      label: 'Preview',
      onClick: (row) => setPreviewCampaign(row),
    },
    {
      label: 'Edit',
      onClick: (row) => {
        if (row.status === 'draft' || row.status === 'scheduled') {
          navigate(`/marketing/campaigns/${row.campaignId}`);
        } else {
          setEditCampaign(row);
        }
      },
    },
    {
      label: 'Delete',
      variant: 'danger',
      onClick: (row) => setDeleteCampaign(row),
      disabled: (row) => row.status !== 'draft' && row.status !== 'scheduled',
    },
  ];

  // ── Handlers ───────────────────────────────────────────────────────────
  async function handleDelete() {
    if (!deleteCampaign) return;
    await deleteMutation.mutateAsync(deleteCampaign.campaignId);
  }

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <>
      <ListPageLayout
        filterSidebar={(
          <FilterSidebar
            filters={FILTER_CONFIGS}
            values={sidebarValues}
            options={sidebarOptions}
            onChange={handleSidebarChange}
            onClear={handleSidebarClear}
            showAIAssistant={false}
          />
        )}
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="shrink-0 space-y-6 p-6">
            <PageHeader
              title="Campaigns"
              subtitle="Create and manage email campaigns"
              actions={
                <Button onClick={() => setShowCreate(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  New Campaign
                </Button>
              }
            />

            <MetricCardGrid columns={4}>
              <StatCard label="Total Campaigns" value={stats.total} />
              <StatCard label="Drafts" value={stats.drafts} />
              <StatCard label="Sent" value={stats.sent} />
              <StatCard label="Total Recipients" value={stats.totalRecipients} />
            </MetricCardGrid>

          </div>

          <div className="min-h-0 flex-1 overflow-auto">
            <DataTable<Campaign>
              data={data?.data ?? []}
              columns={columns}
              keyField="campaignId"
              loading={isLoading}
              rowActions={rowActions}
              onRowClick={handleRowClick}
              page={page}
              pageSize={pageSize}
              total={data?.meta?.total}
              onPageChange={setPage}
              emptyTitle="No campaigns"
              emptyDescription="Create your first email campaign to get started."
            />
          </div>
        </div>
      </ListPageLayout>

      {/* Create modal */}
      {showCreate && (
        <CampaignModal
          onClose={() => setShowCreate(false)}
          onSaved={handleSaved}
        />
      )}

      {/* Edit modal */}
      {editCampaign && (
        <CampaignModal
          campaign={editCampaign}
          onClose={() => setEditCampaign(null)}
          onSaved={handleSaved}
        />
      )}

      {/* Preview modal from table row action */}
      {previewCampaign && (
        <PreviewModal
          subject={previewCampaign.subject}
          body={previewCampaign.emailBody ?? ''}
          fromName={previewCampaign.fromName ?? ''}
          onClose={() => setPreviewCampaign(null)}
        />
      )}

      {/* Delete confirmation */}
      <ConfirmDialog
        isOpen={deleteCampaign !== null}
        title="Delete Campaign"
        message={
          deleteCampaign
            ? `Are you sure you want to delete "${deleteCampaign.name}"? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setDeleteCampaign(null)}
      />
    </>
  );
}
